import { Flag, Loader2, AlertOctagon } from "lucide-react";
import { useFetch } from "./api/useFetch";
import { adaptFeatureFlagsList } from "./api/adapters";
import { isSurfaceLive } from "./api/platformClient";
import { LiveBadge } from "./ui-badges";

export const FeatureFlags = () => {
  const live = isSurfaceLive("FEATURE_FLAGS");
  const { data, error, loading } = useFetch(live ? "/api/platform/feature-flags" : null);
  const flags = data ? adaptFeatureFlagsList(data) : [];

  return (
    <div className="space-y-4" data-testid="feature-flags">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Feature flags
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Read-only view of ShelfLife platform flags. The Control Center does
            not toggle flags.
          </p>
        </div>
        <LiveBadge live={live} />
      </div>

      {!live && (
        <Empty msg="Feature flags surface is disabled by env flag." />
      )}

      {live && loading && <Loading />}
      {live && error && <FetchError error={error} />}

      {live && !loading && !error && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="feature-flags-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Flag</th>
                <th className="text-left px-4 py-3">Enabled</th>
                <th className="text-left px-4 py-3">Scope</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-left px-4 py-3">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {flags.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                    <Flag className="mx-auto h-6 w-6 text-slate-300" />
                    <div className="mt-2">No feature flags returned.</div>
                  </td>
                </tr>
              )}
              {flags.map((f) => (
                <tr key={f.key} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs text-slate-800">{f.key}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
                        f.enabled
                          ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                          : "bg-slate-100 text-slate-600 ring-slate-500/20"
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          f.enabled ? "bg-emerald-500" : "bg-slate-400"
                        }`}
                      />
                      {f.enabled ? "On" : "Off"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <span className="inline-flex items-center text-[11px] uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded">
                      {f.scope}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{f.description || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{f.updatedAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Empty = ({ msg }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
    <Flag className="mx-auto h-8 w-8 text-slate-400" />
    <p className="mt-3 text-sm text-slate-600">{msg}</p>
  </div>
);

const Loading = () => (
  <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
    <Loader2 className="mx-auto h-6 w-6 text-slate-400 animate-spin" />
    <p className="mt-3 text-sm text-slate-500">Loading…</p>
  </div>
);

const FetchError = ({ error }) => (
  <div className="rounded-xl border border-rose-200 bg-rose-50 p-6">
    <div className="flex items-center gap-3">
      <AlertOctagon className="h-5 w-5 text-rose-700" />
      <div className="text-sm font-semibold text-rose-900">
        {error.kind === "forbidden" ? "Not authorized to view feature flags" :
         error.kind === "unauthorized" ? "Session expired" :
         error.kind === "network" ? "Cannot reach preview API" :
         "Feature flags request failed"}
      </div>
    </div>
    <p className="text-xs text-rose-800 mt-2">{error.message}</p>
  </div>
);
